<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api, type ReportsPayload } from "../api";

const emit = defineEmits<{ error: [message: string] }>();
const reports = ref<ReportsPayload | null>(null);
const loading = ref(true);
const maxVolume = computed(() => Math.max(1, ...(reports.value?.weekly.map((week) => Number(week.volume)) ?? [1])));
const volumeLabel = (value: number) => value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(value ?? 0);
const dateLabel = (value: string) => new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(value));
async function load() {
  try { reports.value = await api.reports(); }
  catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not load progress"); }
  finally { loading.value = false; }
}
onMounted(() => { void load(); });
</script>

<template>
  <section class="view progress-view">
    <header class="view-header"><div><p class="eyebrow">Last 12 weeks</p><h1>Progress</h1></div><span class="live-chip"><i></i>LIVE DATA</span></header>
    <div v-if="loading" class="skeleton-stack"><i></i><i></i><i></i></div>
    <template v-else-if="reports">
      <div class="metric-grid">
        <article><span>SESSIONS</span><strong>{{ reports.summary.sessions }}</strong><small>completed</small></article>
        <article><span>WORK SETS</span><strong>{{ reports.summary.sets }}</strong><small>logged</small></article>
        <article><span>VOLUME</span><strong>{{ volumeLabel(reports.summary.volume) }}</strong><small>lb moved</small></article>
        <article><span>EFFORT</span><strong>{{ reports.summary.avg_effort || "—" }}</strong><small>average / 10</small></article>
      </div>
      <section class="chart-card">
        <header><div><p class="eyebrow">Training load</p><h2>Weekly volume</h2></div><span>LB × REPS</span></header>
        <div v-if="reports.weekly.length" class="bar-chart">
          <div v-for="week in reports.weekly" :key="week.week" class="bar-column"><b>{{ volumeLabel(week.volume) }}</b><i :style="{ height: `${Math.max(8, week.volume / maxVolume * 100)}%` }"></i><span>{{ week.week.slice(5) }}</span></div>
        </div>
        <p v-else class="chart-empty">Complete a workout to start the trend.</p>
      </section>
      <div class="report-columns">
        <section class="list-card">
          <header><p class="eyebrow">Estimated strength</p><h2>Exercise bests</h2></header>
          <p v-if="!reports.exercises.length" class="muted">Your top sets will appear here.</p>
          <div v-for="exercise in reports.exercises" :key="exercise.id" class="ranking-row"><span>{{ exercise.name }}</span><b>{{ exercise.estimated_1rm || exercise.best_weight }} lb</b><small>{{ exercise.sets }} sets</small></div>
        </section>
        <section class="list-card">
          <header><p class="eyebrow">History</p><h2>Recent sessions</h2></header>
          <p v-if="!reports.recent.length" class="muted">No completed sessions yet.</p>
          <div v-for="session in reports.recent" :key="session.id" class="history-row"><span>{{ dateLabel(session.started_at) }}</span><div><b>{{ session.title }}</b><small>{{ session.sets }} sets · {{ volumeLabel(session.volume) }} lb</small></div><strong>{{ session.perceived_effort ?? "—" }}</strong></div>
        </section>
      </div>
    </template>
  </section>
</template>
